import { getFilterOptionsFromClassifier } from 'components/tables/list-table/filterUtil';
import { CompositePart, MainFilter, SelectOption } from 'components/tables/list-table/ListTableTypes';
import { useHasSeveralActiveOffices } from 'hooks/useHasSeveralActiveOffices';
import { useLastEffortType } from 'hooks/useLastEffortType';
import { contactFilters } from 'pages/contacts/contactFilters';
import useAuth from 'hooks/useAuth';
import { orderByPriority } from 'pages/debriefing/debriefingForm';
import { inBaseCurrency, useReportBaseCurrency } from 'pages/reporting/reportCurrency';
import { useMemo } from 'react';
import { Setting } from 'types/back-office-settings';
import { ClassifierData } from 'types/classifiers';
import { RANKINGS_REFEREE_TYPE } from 'types/efforts';
import { EmployeeListItemMinDto } from 'types/employees';
import { FeatureFlag } from 'types/features';
import { MarketingProfileFilterField } from 'types/marketingProfiles';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useSettings } from 'utils/useSettings';
import { MarketingProfileFilterDef } from './marketingProfileFormUtils';
import { useAdministratorField } from 'hooks/useAdministratorField';

export const MARKETING_PROFILE_CLASSIFIERS = [
    'RELATIONSHIP_TYPE',
    'SERVICE_LEVEL_TYPE',
    'SEGMENT_TYPE',
    'MAILING_PREFERENCE_TYPE',
    'MARKETING_AREA_TYPE',
    'AREA_OF_RESPONSIBILITY_TYPE',
    'LANGUAGE',
    'COUNTRY',
    'BUSINESS_AREA_TYPE',
    'KEYWORD_TYPE',
    'COOPERATION_AREA_TYPE',
    'MATTER_STATE_TYPE',
    'PERSON_TITLE',
    'EFFORT_ACTIVITY_TYPE',
];

const REFERRAL_READ_PERMISSIONS = ['READ.REFERRALS.ALL'];

const PERSON = 'marketing-profiles.filter-groups.person';
const EMPLOYER = 'marketing-profiles.filter-groups.employer';
const ACTIVITY = 'marketing-profiles.filter-groups.activity';
const OTHER_PROFILES = 'marketing-profiles.filter-groups.other-profiles';

/** The general controls' (i), the same on every filter. */
const CONTROL_TOOLTIPS = {
    exclude: 'marketing-profiles.tooltips.exclude',
    isNot: 'marketing-profiles.tooltips.is-not',
    empty: 'marketing-profiles.tooltips.empty',
    match: 'marketing-profiles.tooltips.match',
};
const EMPTY_COPY = {
    empty: 'marketing-profiles.filters.is-empty-about',
    notEmpty: 'marketing-profiles.filters.is-not-empty-about',
};

/** Contacts' tooltip, as the profile words it: about persons rather than contacts. */
const personsTooltip = (tooltip: string | undefined) =>
    tooltip?.replace('contacts.tooltips.', 'marketing-profiles.tooltips.');

/**
 * The profile panel's filters, group by group: the Contacts list's own definitions where the profile offers the
 * same filter, a list filter of the person always able to exclude, and Contacts' gates kept.
 */
export function useMarketingProfileFilters(
    classifiers: Record<string, ClassifierData | undefined>,
    otherProfiles: SelectOption[]
): MarketingProfileFilterDef[] {
    const lastEffortType = useLastEffortType();
    const hasSeveralActiveOffices = useHasSeveralActiveOffices();
    const isInvoicingDateOnPartyFormEnabled = useIsFeatureEnabled(FeatureFlag.INVOICING_DATE_ON_PARTY_FORM);
    const hasRankingsReferees = useIsFeatureEnabled(FeatureFlag.RANKINGS_REFEREE);
    const { isOn: isAdministratorFieldEnabled, clientLabel: clientAssistantLabel } = useAdministratorField();
    const { user } = useAuth();
    const isTitleFreeText = useSettings().isSettingEnabled(Setting.PERSON_TITLES_FREE_TEXT);
    const currency = useReportBaseCurrency();
    const canReadReferrals = useHasPermissions(REFERRAL_READ_PERMISSIONS);

    return useMemo(() => {
        const c = contactFilters({
            classifiers,
            relationshipTypeOptions: [],
            activityType: lastEffortType?.name,
            clientAssistantLabel,
        });
        const EMPTINESS = {
            nullOption: { label: t('filters.is-empty-option') },
            notEmptyOption: { label: t('filters.is-not-empty-option') },
        };
        const def = (
            field: MarketingProfileFilterField,
            group: string,
            filter: MainFilter,
            overrides: Partial<MainFilter> = {}
        ): MarketingProfileFilterDef => ({
            field,
            filter: {
                ...filter,
                group,
                controlTooltips: CONTROL_TOOLTIPS,
                emptyCopy: EMPTY_COPY,
                tooltip: personsTooltip(filter.tooltip),
                ...(filter.type === 'checkboxgroup'
                    ? { filters: filter.filters.map((box) => ({ ...box, tooltip: personsTooltip(box.tooltip) })) }
                    : {}),
                ...overrides,
            } as MainFilter,
        });
        const person = (field: MarketingProfileFilterField, filter: MainFilter, overrides?: Partial<MainFilter>) =>
            def(field, PERSON, filter, overrides);
        const employer = (field: MarketingProfileFilterField, filter: MainFilter, overrides?: Partial<MainFilter>) =>
            def(field, EMPLOYER, filter, overrides);
        const activity = (field: MarketingProfileFilterField, filter: MainFilter, overrides?: Partial<MainFilter>) =>
            def(field, ACTIVITY, filter, overrides);
        const copy = (key: string) => t(`marketing-profiles.filters.${key}`);
        const between = (label: string): CompositePart => ({
            type: 'daterange',
            label: copy(label),
            selector: 'between',
            shouldDisplayQuickPick: false,
        });
        const exclude = { canExclude: true };
        // A Contacts list filter of the person read through their employers: its control, the employer's operator and
        // copy, and an exclusion as every list filter has.
        const through = (comperator: string, label: string | undefined, tooltip: string): Partial<MainFilter> => ({
            selector: 'id',
            comperator,
            ...(label ? { label: t(`marketing-profiles.filters.${label}`) } : {}),
            tooltip: `marketing-profiles.tooltips.${tooltip}`,
            canExclude: true,
        });
        // A value recorded or not: two answers, which the new design's cards and chip name by their labels, each card
        // described by `about` where the design writes a line under it.
        const presence = (
            comperator: string,
            name: string,
            yes: string,
            no: string,
            about?: { yes: string; no: string }
        ): MainFilter => ({
            type: 'checkboxgroup',
            label: t(`marketing-profiles.filters.${name}`),
            selector: 'id',
            comperator,
            tooltip: `marketing-profiles.tooltips.${name}`,
            namedAnswers: true,
            yesNoCopy: { help: `marketing-profiles.tooltips.${name}`, ...about },
            filters: [
                { label: t(`marketing-profiles.filters.${yes}`), argument: 'true' },
                { label: t(`marketing-profiles.filters.${no}`), argument: 'false' },
            ],
        });
        // The signed-in colleague heads an employee list.
        const meFirst = (filter: MainFilter): Partial<MainFilter> =>
            filter.type === 'dynamicmultiselect'
                ? {
                      paginated: {
                          ...filter.paginated,
                          transformOptions: (options: EmployeeListItemMinDto[], search: string) =>
                              user ? orderByPriority(options, [user], search) : options,
                      },
                  }
                : {};

        return [
            // Enter adds a term, as the profile's other term fields do.
            person('NAME', c.name, {
                label: t('marketing-profiles.filters.name'),
                matchModes: true,
                commitOnEnter: true,
            }),
            person('POSITION', c.position, {
                label: t('marketing-profiles.filters.position'),
                matchModes: true,
                emptyComparator: '=hasposition=',
            }),
            isTitleFreeText
                ? person('PERSON_TITLE_TEXT', {
                      type: 'text',
                      label: t('marketing-profiles.filters.person-title'),
                      selector: 'id',
                      comperator: '=persontitlelk=',
                      tooltip: 'marketing-profiles.tooltips.person-title-text',
                      multiple: true,
                      commitOnEnter: true,
                      matchModes: true,
                      emptyComparator: '=haspersontitle=',
                  })
                : person('PERSON_TITLE', {
                      type: 'multiselect',
                      label: t('marketing-profiles.filters.person-title'),
                      selector: 'personTitleCode',
                      tooltip: 'marketing-profiles.tooltips.person-title-list',
                      canExclude: true,
                      options: getFilterOptionsFromClassifier(classifiers['PERSON_TITLE']),
                      ...EMPTINESS,
                  }),
            // The design's names, where a shared Contacts filter keeps its own in Contacts.
            person('AREA_OF_RESPONSIBILITY', c.areasOfResponsibility, {
                ...EMPTINESS,
                label: copy('areas-of-responsibility'),
            }),
            person('PERSON_KEYWORD', c.keywords, { label: t('marketing-profiles.filters.person-keywords') }),
            person('RELATIONSHIP_TYPE', {
                type: 'multiselect',
                label: t('contacts.about.relationship-type'),
                selector: 'relationshipTypeCode',
                tooltip: 'marketing-profiles.tooltips.relationship-type',
                canExclude: true,
                options: getFilterOptionsFromClassifier(classifiers['RELATIONSHIP_TYPE']),
            }),
            person('BUSINESS_CONTACT_OF', c.businessContactOf, meFirst(c.businessContactOf)),
            person('MAILING_PREFERENCE', c.mailingPreferences, { label: copy('mailing-preferences') }),
            person('MARKETING_AREA', c.marketingAreas, { ...EMPTINESS, label: copy('preferred-marketing-areas') }),
            person('PREFERRED_MAILING_LANGUAGE', c.preferredMailingLanguage, {
                ...exclude,
                ...EMPTINESS,
                label: copy('preferred-mailing-language'),
            }),
            person('JURISDICTION', c.jurisdictions, { label: copy('jurisdictions-of-interest') }),
            person('COUNTRY', c.country),
            person('CITY', c.city, { tooltip: 'marketing-profiles.tooltips.city', commitOnEnter: true }),
            person(
                'EMAIL',
                presence('=hasemail=', 'email', 'has-email', 'has-no-email', {
                    yes: 'marketing-profiles.filters.has-email-about',
                    no: 'marketing-profiles.filters.has-no-email-about',
                })
            ),
            person('PHONE', presence('=hasphone=', 'phone', 'has-phone', 'has-no-phone')),
            person('CREATED_DATE', c.createdDate, {
                tooltip: 'marketing-profiles.tooltips.created-date',
                label: copy('created-date'),
                labelTo: undefined,
            }),
            person('EXCLUDE_PERSONA_NON_GRATA', c.excludePersonaNonGrata),
            // The Contacts filters the profile's design does not draw, after the drawn ones in Contacts' order.
            person('MY_CLIENTS', c.myClients),
            person('MY_CONTACTS', c.myContacts),
            person('INTERNAL_CLIENT', c.internalClient),
            ...(isInvoicingDateOnPartyFormEnabled
                ? [person('INVOICE_DATE_CURRENT_DATE', c.invoiceDateCurrentDate)]
                : []),
            person('RELATIONSHIP_PARTNER', c.relationshipPartner),
            person('RELATIONSHIP_MANAGER', c.relationshipManager),
            ...(isAdministratorFieldEnabled ? [person('CLIENT_ASSISTANT', c.clientAssistant)] : []),
            person('BUSINESS_AREA', c.businessArea),
            person('SECTOR_GROUP', c.sectorGroup),
            person('SEGMENT', c.segment),
            person('SERVICE_LEVEL', c.serviceLevel),
            ...(hasSeveralActiveOffices
                ? [
                      person('CASES_HANDLED_BY_OFFICE', c.casesHandledByOffice),
                      person('NO_RECENT_WORK_BY_OFFICE', c.noRecentWorkByOffice),
                      person('NEVER_SERVED_BY_OFFICE', c.neverServedByOffice),
                  ]
                : []),
            person('CASES_IN_PRACTICE_GROUP', c.casesInPracticeGroup),
            person('NO_RECENT_WORK_BY_PRACTICE_GROUP', c.noRecentWorkByPracticeGroup),
            person('NEVER_SERVED_BY_PRACTICE_GROUP', c.neverServedByPracticeGroup),
            person('MEMBER_OF_GROUP', c.memberOfGroup),
            person('COOPERATION_PARTNER', c.cooperationPartner),
            person('NOTES', c.notes),
            person('CLIENT_SINCE', c.clientSince),
            person('CASE_LAST_TIMECARD_DATE', c.caseLastTimecardDate),
            person('CASE_STATUS', c.caseStatus),
            person('CASE_SUPERVISING_PARTNER', c.caseSupervisingPartner),
            person('CASE_RESPONSIBLE_LAWYER', c.caseResponsibleLawyer),
            person('CASE_OPENING_DATE', c.caseOpeningDate),
            person('OPPONENT', c.opponent),
            person('SELECTED_AS_JUDGE', c.selectedAsJudge),
            person('ACTIVE_CASES_NO_AGREEMENT', c.activeCasesNoAgreement),
            person('INACTIVE_RELATIONSHIP_PARTNER', c.inactiveRelationshipPartner),
            person('INACTIVE_RELATIONSHIP_MANAGER', c.inactiveRelationshipManager),
            person('ADDING_CASES_BLOCKED', c.addingCasesBlocked),
            person('ADDING_TIMECARDS_BLOCKED', c.addingTimecardsBlocked),
            employer('EMPLOYER', c.employer, {
                ...EMPTINESS,
                emptyComparator: '=hasemployer=',
                label: copy('employer'),
            }),
            employer('EMPLOYER_RELATIONSHIP_TYPE', {
                type: 'multiselect',
                label: t('marketing-profiles.filters.employer-relationship-type'),
                selector: 'id',
                comperator: '=hasemployerrelationshiptype=',
                tooltip: 'marketing-profiles.tooltips.employer-relationship-type',
                canExclude: true,
                options: getFilterOptionsFromClassifier(classifiers['RELATIONSHIP_TYPE']),
            }),
            employer('EMPLOYER_COUNTRY', c.employerCountry, { label: copy('employer-country') }),
            employer('EMPLOYER_CITY', {
                type: 'text',
                label: t('marketing-profiles.filters.employer-city'),
                selector: 'id',
                comperator: '=hasemployercitylk=',
                tooltip: 'marketing-profiles.tooltips.employer-city',
                multiple: true,
                commitOnEnter: true,
            }),
            employer('EMPLOYER_BUSINESS_AREA', c.employerBusinessArea, { label: copy('employer-business-area') }),
            employer(
                'EMPLOYER_SECTOR_GROUP',
                c.sectorGroup,
                through('=hasemployersectorgroup=', 'employer-sector-group', 'employer-sector-group')
            ),
            employer('EMPLOYER_SEGMENT', c.employerSegment, { label: copy('employer-segment') }),
            employer('EMPLOYER_SERVICE_LEVEL', c.employerServiceLevel, { label: copy('employer-service-level') }),
            employer('EMPLOYER_KEYWORD', c.employerKeyword, {
                label: t('marketing-profiles.filters.company-keywords'),
            }),
            employer('EXCLUDE_EMPLOYED_BY_INTERNAL_CLIENT', {
                ...c.excludeEmployedByInternalClient,
                filters: c.excludeEmployedByInternalClient.filters.map((box) => ({
                    ...box,
                    label: copy('exclude-employed-by-internal-client'),
                })),
            }),
            employer('EMPLOYER_REVENUE', {
                type: 'composite',
                label: copy('employer-revenue'),
                selector: 'id',
                comperator: '=employerrevenue=',
                tooltip: 'marketing-profiles.tooltips.employer-revenue',
                parts: [
                    {
                        type: 'interval',
                        selector: 'amount',
                        minLabel: inBaseCurrency(t('from'), currency),
                        maxLabel: inBaseCurrency(t('to'), currency),
                        minTooltip: 'marketing-profiles.tooltips.employer-revenue',
                        minPlaceholder: 'marketing-profiles.filters.amount',
                        maxPlaceholder: 'marketing-profiles.filters.amount',
                    },
                    {
                        type: 'dropdown',
                        label: copy('period'),
                        selector: 'period',
                        options: [
                            { value: 'LAST_12_MONTHS', label: copy('last-12-months') },
                            { value: 'LAST_3_YEARS', label: copy('last-3-years') },
                        ],
                    },
                ],
            }),
            employer('EMPLOYER_CLIENT_SINCE', c.clientSince, {
                selector: 'id',
                comperator: '=employerclientsince=',
                label: copy('employer-client-since'),
                labelTo: undefined,
                tooltip: 'marketing-profiles.tooltips.employer-client-since',
            }),
            employer('EMPLOYER_RELATIONSHIP_OWNER', c.relationshipPartner, {
                ...through(
                    '=hasemployerrelationshipowner=',
                    'employer-relationship-owner',
                    'employer-relationship-owner'
                ),
                ...meFirst(c.relationshipPartner),
            } as Partial<MainFilter>),
            ...(hasSeveralActiveOffices
                ? [
                      employer(
                          'EMPLOYER_CASES_HANDLED_BY_OFFICE',
                          c.casesHandledByOffice,
                          through(
                              '=hasemployercasesbyoffice=',
                              'employer-cases-handled-by-office',
                              'employer-cases-handled-by-office'
                          )
                      ),
                  ]
                : []),
            employer(
                'EMPLOYER_CASES_IN_PRACTICE_GROUP',
                c.casesInPracticeGroup,
                through(
                    '=hasemployercasesinpracticegroup=',
                    'employer-cases-in-practice-group',
                    'employer-cases-in-practice-group'
                )
            ),
            ...(hasSeveralActiveOffices
                ? [
                      employer('EMPLOYER_NO_RECENT_WORK_BY_OFFICE', c.noRecentWorkByOffice, {
                          ...through('=employernorecentworkbyoffice=', undefined, 'employer-no-recent-work'),
                      }),
                  ]
                : []),
            employer('EMPLOYER_NO_RECENT_WORK_BY_PRACTICE_GROUP', c.noRecentWorkByPracticeGroup, {
                ...through('=employernorecentworkbypracticegroup=', undefined, 'employer-no-recent-work'),
            }),
            ...(hasSeveralActiveOffices
                ? [
                      employer('EMPLOYER_NEVER_SERVED_BY_OFFICE', c.neverServedByOffice, {
                          ...through('=employerneverservedbyoffice=', undefined, 'employer-never-served'),
                      }),
                  ]
                : []),
            employer('EMPLOYER_NEVER_SERVED_BY_PRACTICE_GROUP', c.neverServedByPracticeGroup, {
                ...through('=employerneverservedbypracticegroup=', undefined, 'employer-never-served'),
            }),
            employer(
                'EMPLOYER_CASE_STATUS',
                c.caseStatus,
                through('=hasemployercasestatus=', 'employer-case-status', 'employer-case-status')
            ),
            employer(
                'EMPLOYER_CASE_LAWYER',
                c.caseSupervisingPartner,
                through('=hasemployercaselawyer=', 'employer-case-lawyers', 'employer-case-lawyers')
            ),
            employer(
                'EMPLOYER_MEMBER_OF_GROUP',
                c.memberOfGroup,
                through('=isemployermemberofgroup=', 'employer-member-of-group', 'employer-member-of-group')
            ),
            employer(
                'EMPLOYER_ADDRESS',
                presence('=hasemployeraddress=', 'employer-address', 'has-employer-address', 'has-no-employer-address')
            ),
            employer('EXCLUDE_EMPLOYER_OPPONENT', {
                type: 'checkboxgroup',
                selector: 'id',
                comperator: '=excludeemployeropponent=',
                filters: [
                    {
                        label: t('marketing-profiles.filters.exclude-employer-opponent'),
                        tooltip: 'marketing-profiles.tooltips.exclude-employer-opponent',
                        argument: 'true',
                    },
                ],
            }),
            // One label over both dates.
            activity('LAST_BD_EFFORT_FROM', c.lastBdEffortFrom, { labelTo: undefined }),
            activity('LAST_BD_EFFORT_OLDER_THAN', c.lastBdEffortOlderThan),
            ...(lastEffortType
                ? [
                      activity('LAST_CONFIGURED_EFFORT_FROM', c.lastEffortActivityFrom, { labelTo: undefined }),
                      activity('LAST_CONFIGURED_EFFORT_OLDER_THAN', c.lastEffortActivityOlderThan),
                  ]
                : []),
            activity('HAD_BD_EFFORT', {
                type: 'composite',
                label: copy('had-bd-effort'),
                selector: 'id',
                comperator: '=hadbdeffort=',
                tooltip: 'marketing-profiles.tooltips.had-bd-effort',
                canExclude: true,
                parts: [
                    {
                        type: 'multiselect',
                        label: copy('effort-types'),
                        selector: 'values',
                        // Rankings referee is a type only with its feature.
                        options: getFilterOptionsFromClassifier(classifiers['EFFORT_ACTIVITY_TYPE']).filter(
                            ({ value }) => hasRankingsReferees || value !== RANKINGS_REFEREE_TYPE
                        ),
                    },
                    between('between'),
                    {
                        type: 'number',
                        label: copy('at-least'),
                        selector: 'atLeast',
                        min: 1,
                        chipText: 'marketing-profiles.filters.at-least-chip',
                    },
                ],
            }),
            activity('NO_BD_EFFORT_IN_MONTHS', {
                type: 'number',
                label: copy('no-bd-effort-in-months'),
                selector: 'id',
                comperator: '=nobdeffortinmonths=',
                tooltip: 'marketing-profiles.tooltips.no-bd-effort-in-months',
                min: 1,
            }),
            // The backend refuses the clause to a user who may not read referrals.
            ...(canReadReferrals
                ? [
                      activity('REFERRALS', {
                          type: 'composite',
                          label: copy('referrals'),
                          selector: 'id',
                          comperator: '=referral=',
                          tooltip: 'marketing-profiles.tooltips.referrals',
                          parts: [
                              {
                                  type: 'checkboxgroup',
                                  selector: 'direction',
                                  filters: [
                                      { label: copy('gave-referral'), argument: 'GAVE' },
                                      { label: copy('received-referral'), argument: 'RECEIVED' },
                                  ],
                              },
                              between('referred-between'),
                          ],
                      }),
                  ]
                : []),
            def('IN_MARKETING_PROFILE', OTHER_PROFILES, {
                type: 'multiselect',
                label: copy('in-marketing-profile'),
                selector: 'id',
                comperator: '=inmarketingprofile=',
                tooltip: 'marketing-profiles.tooltips.in-marketing-profile',
                canExclude: true,
                // The criteria carry it to the preview: no party operator reads a profile.
                virtual: true,
                options: otherProfiles,
            }),
        ];
    }, [
        classifiers,
        lastEffortType,
        hasSeveralActiveOffices,
        isInvoicingDateOnPartyFormEnabled,
        hasRankingsReferees,
        isAdministratorFieldEnabled,
        clientAssistantLabel,
        user,
        isTitleFreeText,
        currency,
        canReadReferrals,
        otherProfiles,
    ]);
}
